import type { DropEvent, FileDropItem } from "react-aria";

import {
  BRAND_GUIDELINE_PDF_MAX_SIZE_MB,
  GUIDELINE_CONTENT_TYPES,
} from "@nlc/shared-types";
import { AnimatePresence, motion } from "framer-motion";
import { CloudUpload, Download, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { DropZone, FileTrigger } from "react-aria-components";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue";
import { useBrandPresignedUpload } from "@/features/brands/shared/hooks/useBrandPresignedUpload";

import { useCreateBrandFormContext } from "../../components/BrandsForm";
import { CreateBrandPageHeader } from "../shared/components/header/CreateBrandPageHeader";
import { BackIcon } from "../shared/components/icons/BackIcon";
import { InfoCallout } from "../shared/components/info/InfoCallout";

export function BrandGuidelines({
  onBack,
  onNext,
}: {
  onBack: () => void;
  onNext: () => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);

  return (
    <>
      <CreateBrandPageHeader
        description="Share your design standards for better consistency"
        title="Brand guidelines"
      />

      <InfoCallout
        description="Your brand manual helps our AI understand your visual identity, tone of voice, and design rules, ensuring everything we create feels like you."
        title="Why is this helpful?"
      />

      <div className="mt-2 w-full">
        <div className="w-full">
          <BrandGuidelinesUploader
            onErrorChange={setHasUploadError}
            onUploadingChange={setIsUploading}
          />
        </div>
      </div>

      <GuidelinesActions
        hasError={hasUploadError}
        isUploading={isUploading}
        onBack={onBack}
        onNext={onNext}
      />
    </>
  );
}

function BrandGuidelinesUploader({
  onErrorChange,
  onUploadingChange,
}: {
  onErrorChange: (hasError: boolean) => void;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const form = useCreateBrandFormContext();
  const { error, isUploading, upload } = useBrandPresignedUpload();

  useEffect(() => {
    onUploadingChange(isUploading);
  }, [isUploading, onUploadingChange]);

  useEffect(() => {
    onErrorChange(!!error);
    if (error) {
      toast.add({
        description: error,
        timeout: 4000,
        title: "File too large",
        type: "error",
      });
    }
  }, [error, onErrorChange]);

  const [file, setFile] = useState<File | null>(null);
  const [isDropTarget, setIsDropTarget] = useState(false);

  const handleFile = async (selectedFile: File) => {
    setFile(selectedFile);
    form.setValue("guidelineS3Key", null);

    const s3Key = await upload(selectedFile, "guideline");

    if (s3Key) {
      form.setValue("guidelineS3Key", s3Key);
    } else {
      setFile(null);
    }
  };

  const handleRemove = () => {
    setFile(null);
    form.setValue("guidelineS3Key", null);
  };

  const handleDrop = async (e: DropEvent) => {
    const fileItems = e.items.filter(
      (item): item is FileDropItem => item.kind === "file",
    );

    const validItem = fileItems.find((item) =>
      (GUIDELINE_CONTENT_TYPES as readonly string[]).includes(item.type),
    );

    if (validItem) {
      const droppedFile = await validItem.getFile();

      handleFile(droppedFile);
    } else if (fileItems.length > 0) {
      toast.add({
        description: "Only PDF files are accepted.",
        timeout: 3000,
        title: "Unsupported file type",
        type: "error",
      });
    }
  };

  return (
    <AnimatePresence mode="wait">
      {file ? (
        <motion.div
          animate={{ opacity: 1, originX: 0, scaleX: 1 }}
          className="flex w-full items-center justify-between rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm"
          exit={{
            opacity: 0,
            originX: 0,
            scaleX: 0,
            transition: { duration: 0.2 },
          }}
          initial={{ opacity: 0, originX: 0, scaleX: 0 }}
          key="file-uploader"
          transition={{ duration: 0.4, ease: "circOut" }}
        >
          <span className="text-sm font-medium text-text-primary truncate flex-1 mr-4">
            {isUploading ? "Uploading..." : file.name}
          </span>
          <div className="flex items-center gap-2">
            <Button
              className="text-gray-400 hover:text-gray-600"
              isDisabled={isUploading}
              isIconOnly
              onPress={handleRemove}
              size="sm"
              variant="tertiary"
            >
              <Trash2 size={20} />
            </Button>
            <Button
              className="text-violet-600 hover:text-violet-700"
              isIconOnly
              onPress={() => {
                const url = URL.createObjectURL(file);
                const a = document.createElement("a");

                a.href = url;
                a.download = file.name || "download";
                a.click();
                URL.revokeObjectURL(url);
              }}
              size="sm"
              variant="tertiary"
            >
              <Download size={20} />
            </Button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          animate={{ opacity: 1 }}
          className="w-full"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          key="drop-zone"
        >
          <DropZone
            className={`group flex w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border transition-all duration-200 py-8 ${
              isDropTarget
                ? "border-border-brand-solid bg-bg-brand-secondary"
                : error
                  ? "border-border-error bg-bg-error-secondary"
                  : "bg-bg-tertiary border-border-secondary hover:bg-bg-secondary-hover"
            }`}
            onDrop={handleDrop}
            onDropEnter={() => setIsDropTarget(true)}
            onDropExit={() => setIsDropTarget(false)}
          >
            <FileTrigger
              acceptedFileTypes={[".pdf"]}
              onSelect={(files) => {
                const selected = files ? [...files][0] : null;

                if (selected) handleFile(selected);
              }}
            >
              <Button className="w-full outline-hidden" variant="unstyled">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`grid size-10 place-items-center rounded-lg border mb-1 ${error ? "bg-bg-error-secondary border-border-error" : "bg-bg-secondary border-border-secondary"}`}
                  >
                    <CloudUpload
                      className={
                        error ? "text-text-error-primary" : "text-text-tertiary"
                      }
                      size={20}
                    />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-sm text-text-secondary">
                      <span
                        className={`font-semibold ${error ? "text-text-error-primary" : "text-text-brand-primary"}`}
                      >
                        Click to upload
                      </span>{" "}
                      or drag and drop
                    </span>
                    <span
                      className={`text-xs ${error ? "text-text-error-primary" : "text-text-tertiary"}`}
                    >
                      {error ??
                        `PDF up to ${BRAND_GUIDELINE_PDF_MAX_SIZE_MB}MB`}
                    </span>
                  </div>
                </div>
              </Button>
            </FileTrigger>
          </DropZone>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function GuidelinesActions({
  hasError,
  isUploading,
  onBack,
  onNext,
}: {
  hasError: boolean;
  isUploading: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex flex-col items-center w-full">
      <Button
        className="w-full mt-4"
        isDisabled={isUploading || hasError}
        onPress={() => onNext()}
        size="xl"
        type="button"
        variant="primary"
      >
        Continue
      </Button>

      <Button
        className="w-full mt-4 mb-4 text-text-secondary"
        isDisabled={isUploading}
        onPress={() => onNext()}
        size="xl"
        type="button"
        variant="plain"
      >
        Skip this step
      </Button>

      <BackIcon action={onBack} />
    </div>
  );
}
